import React, { useMemo, useState } from 'react';
import { Popover } from 'antd';
import Button from 'components/Button';
import InputSearchSuffixIcon from 'components/InputSearchSuffixIcon';
import appointmentActions from 'features/appointment/services/actions';
import appointmentSelectors from 'features/appointment/services/selectors';
import { pickStaffsByIds } from 'features/appointment/utils/pin-staffs-to-front';
import shopSelectors from 'services/shop/selectors';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import StaffPickerGrid from './staff-picker-grid';
import StaffSearchTriggerBox from './staff-search-trigger-box';
import useStaffNameSearch from './use-staff-name-search';

const SelectStaff = () => {
  const dispatch = useAppDispatch();
  const staffListStore = appointmentSelectors.getListStaffAvailableBooking();
  const appliedIds = appointmentSelectors.staffFilterIds();
  // Danh sách toàn shop — nạp một lần lúc bootstrap, không đổi theo ngày đang
  // xem cũng không bị phân trang, nên chips không nhấp nháy mỗi lần đổi ngày.
  const shopStaffs = shopSelectors.data.staffs();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Draft chỉ sống trong popover — calendar chỉ đổi khi bấm OK.
  const [draftIds, setDraftIds] = useState<string[]>(appliedIds);

  const staffList = useMemo(
    () => staffListStore?.filter((o) => !o.anyBody) || [],
    [staffListStore]
  );

  const visibleStaffs = useStaffNameSearch(staffList, search);

  // Chips là phần hiển thị danh tính nên tra trong danh sách toàn shop: staff
  // vẫn còn chip kể cả ngày đang xem họ không làm, và avatar/tên luôn mới nhất.
  const selectedStaffs = useMemo(
    () => pickStaffsByIds(shopStaffs.length ? shopStaffs : staffList, appliedIds),
    [shopStaffs, staffList, appliedIds]
  );

  // Mở popover = lấy lại trạng thái đã apply, nên đóng bằng click ra ngoài
  // đồng nghĩa huỷ mọi thay đổi chưa OK.
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftIds(appliedIds);
      setSearch('');
    }
    setOpen(nextOpen);
  };

  // Append vào cuối để giữ đúng thứ tự user tick — đó cũng là thứ tự cột.
  const handleToggle = (id: string) =>
    setDraftIds((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id]
    );

  const handleOk = () => {
    dispatch(appointmentActions.setStaffFilterIds(draftIds));
    setSearch('');
    setOpen(false);
  };

  // Đường duy nhất để bỏ hết pin, vì OK bị disable khi không còn ai được chọn.
  const handleClear = () => {
    setDraftIds([]);
    dispatch(appointmentActions.setStaffFilterIds([]));
    setSearch('');
    setOpen(false);
  };

  return (
    <Popover
      placement='bottomLeft'
      arrow={false}
      overlayInnerStyle={{ padding: 0 }}
      content={
        <ContentContainer>
          <SearchBar>
            <InputSearchSuffixIcon value={search} onChange={setSearch} />
          </SearchBar>
          <GridScroll>
            <StaffPickerGrid
              staffs={visibleStaffs}
              selectedIds={draftIds}
              onToggle={handleToggle}
            />
          </GridScroll>
          <Footer>
            <Button
              ntype='DEFAULT'
              disabled={!appliedIds.length && !draftIds.length}
              onClick={handleClear}
              cardCashier
              width='8rem'
            >
              Clear
            </Button>
            <Button
              cardCashier
              width='8rem'
              ntype='PRIMARY' disabled={!draftIds.length} onClick={handleOk}>
              OK
            </Button>
          </Footer>
        </ContentContainer>
      }
      trigger='click'
      open={open}
      onOpenChange={handleOpenChange}
    >
      <StaffSearchTriggerBox staffs={selectedStaffs} />
    </Popover>
  );
};

export default SelectStaff;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 60vw;
  height: 60vh;
`;

const SearchBar = styled.div`
  flex-shrink: 0;
  display: flex;
  padding: 12px 12px 0;
`;

const GridScroll = styled.div`
  flex: 1;
  padding: 12px;
  overflow: auto;
`;

const Footer = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px;
  border-top: 1px solid #e9edf2;
  background: #fff;
`;
